// src/services/goals/goalsService.ts
import { supabase } from '@/lib/supabase';

export type GoalType = 'economic' | 'clients';

export interface Goal {
    id: string;
    shop_id: number;
    type: GoalType;
    title: string | null;
    target: number;
    deadline: string | null;
    note: string | null;
    description: string | null;
    status: string;
    created_at: string;
    // Piano accettato dall'utente (proposto dall'AI, parametri modificabili)
    plan_final_price: number | null;
    plan_monthly_amount: number | null;
    plan_interest_rate: number | null;
    plan_duration_months: number | null;
    plan_cadence: string | null; // 'monthly'
    plan_start_date: string | null;
    plan_accepted_at: string | null;
    next_check_date: string | null;
    // Analisi AI persistita (JSON del piano proposto), calcolata alla creazione
    ai_analysis: Record<string, unknown> | null;
    ai_analyzed_at: string | null;
}

export interface GoalCheck {
    id: string;
    goal_id: string;
    shop_id: number;
    period_start: string | null;
    period_end: string | null;
    status: 'done' | 'missed';
    note: string | null;
    created_at: string;
}

export const fetchGoalsService = async (shopId: number) => {
    const { data, error } = await supabase
        .from('Goals')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });
    if (error) return { success: false, error: error.message, data: [] as Goal[] };
    return { success: true, data: (data || []) as Goal[] };
};

export const createGoalService = async (payload: {
    shop_id: number;
    type: GoalType;
    title?: string | null;
    target: number;
    deadline?: string | null;
    note?: string | null;
    description?: string | null;
}) => {
    const { data, error } = await supabase.from('Goals').insert(payload).select().single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: data as Goal };
};

export const updateGoalService = async (id: string, patch: Partial<Omit<Goal, 'id' | 'shop_id' | 'created_at'>>) => {
    const { data, error } = await supabase.from('Goals').update(patch).eq('id', id).select().single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: data as Goal };
};

export const deleteGoalService = async (id: string) => {
    const { error } = await supabase.from('Goals').delete().eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
};

// ===== Check periodici (es. "rata pagata questo mese") =====

export const fetchGoalChecksService = async (shopId: number) => {
    const { data, error } = await supabase
        .from('GoalChecks')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });
    if (error) return { success: false, error: error.message, data: [] as GoalCheck[] };
    return { success: true, data: (data || []) as GoalCheck[] };
};

export const createGoalCheckService = async (payload: {
    goal_id: string;
    shop_id: number;
    period_start?: string | null;
    period_end?: string | null;
    status: 'done' | 'missed';
    note?: string | null;
}) => {
    const { data, error } = await supabase.from('GoalChecks').insert(payload).select().single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: data as GoalCheck };
};

// ===== Coerenza del piano: importo ↔ rata ↔ durata =====
// I tre parametri del piano sono legati dall'identità  totale = rata × mesi.
// Per gli obiettivi economici il totale è il prezzo maggiorato degli interessi;
// per quelli clienti è direttamente il numero di clienti da acquisire.

/** Totale da coprire: prezzo base + interessi (%). */
export const planTotalAmount = (basePrice: number, interestPct: number): number =>
    basePrice > 0 ? basePrice * (1 + (interestPct || 0) / 100) : 0;

/** Mesi necessari per coprire il totale con una data rata (arrotondati per eccesso, min 1). */
export const monthsForRata = (total: number, rata: number): number =>
    total > 0 && rata > 0 ? Math.max(1, Math.ceil(total / rata)) : 0;

/** Rata necessaria per coprire il totale nel numero di mesi indicato. */
export const rataForMonths = (total: number, months: number): number =>
    total > 0 && months > 0 ? total / months : 0;

/** Aggiunge un mese (in cadenza mensile) a una data yyyy-mm-dd. */
export const nextCheckDateAfter = (dateStr: string): string => {
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
